import React from "react";

import {
  Watch as WatchLater,
  CameraFront as CameraFrontIcon,
  Person as PersonIcon,
} from "@mui/icons-material";
import { Avatar, Box, Button, Theme, Typography } from "@mui/material";
import { makeStyles, withStyles } from "tss-react/mui";

import { Features } from "@/config/features";
import { graphql } from "@/gql";
import { useFragment } from "@/hooks/useFragment";
import { colors } from "@/theme/overrides/colors";
import { format } from "@/utils/datetime";

import useAppointmentCurrent from "../utils/useAppointmentCurrent";

const StyledButton = withStyles(Button, (theme) => ({
  contained: {
    lineHeight: 1.1,
    height: theme.typography.pxToRem(36),
    backgroundColor: "transparent",
    borderWidth: 2,
    borderStyle: "solid",
    borderColor: "#D4DBE5",
    fontWeight: 700,
    color: colors.primary,
    fontSize: "12px",
    "&:hover": {
      backgroundColor: "transparent",
    },
  },
}));

const CancelButton = withStyles(StyledButton, () => ({
  contained: {
    borderColor: "#FF6262",
    color: "#FF6262",
    fontSize: "12px",
  },
}));

const useStyles = makeStyles()((theme: Theme) => ({
  actions: {
    marginTop: 10,
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "center",
    gap: 10,
  },
  aDescription: {
    marginTop: 4,
    fontSize: 12,
    fontWeight: 600,
    color: colors.grey,
  },
  appointmentTime: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-around",
  },
  aTitle: {
    marginTop: 4,
    fontSize: 14,
    fontWeight: 700,
    color: colors.grey,
  },
  avatar: {
    width: 80,
    height: 80,
    borderStyle: "solid",
    borderWidth: 2,
    borderColor: colors.white,
  },
  badge: {
    lineHeight: 0.9,
    fontSize: "10px",
    color: colors.grey,
    borderColor: colors.grey,
    "&:hover": {
      color: colors.grey,
      borderColor: colors.grey,
    },
  },
  borderedContainer: {
    borderStyle: "solid",
    borderTopWidth: 0,
    borderLeftWidth: 1,
    borderBottomWidth: 1,
    borderRightWidth: 1,
    borderColor: "#EDF0F5",
  },
  contact: {
    fontWeight: 700,
    color: colors.grey,
  },
  container: {
    overflow: "hidden",
  },
  contentSpacing: {
    paddingInline: 20,
    paddingBlock: 10,
  },
  dateTime: {
    fontSize: 16,
    fontWeight: 700,
  },
  detailsContainer: {
    width: "50%",
  },
  header: {
    backgroundColor: "#FFE4A9",
  },
  headerTitle: {
    fontSize: 16,
    fontWeight: 700,
  },
  name: {
    fontSize: 16,
    fontWeight: 700,
  },
  profile: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-evenly",
    backgroundColor: "#FFF6E1",
    flexWrap: "wrap",
    paddingBlock: 20,
  },
  typeIcon: {
    width: 15,
  },
  typeIndicator: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    paddingInline: 4,
    borderWidth: 1,
    borderRadius: 24,
    borderStyle: "solid",
    borderColor: "#949BA8",
  },
  typeIndicatorText: {
    fontSize: 10,
    color: "#949BA8",
  },
}));

interface Props {
  useEdit?: boolean;
  onEdit?: () => void;
  onClickMenu?: (type: string, id: string) => void;
  onJoin?: () => void;
  id: string;
}

const appointmentPanelFragment = graphql(`
  fragment AppointmentPanel on Appointment {
    id
    appointmentType
    title
    details
    status
    startDate
    startTime
    endDate
    endTime
    meetingDetails {
      roomUrl
    }
    client {
      id
      email
      profile {
        id
        fullName
        mobileNumber
        avatarUrl(variant: THUMB)
      }
    }
  }
`);

const AppointmentPanel: React.FC<Props> = ({
  id,
  onEdit,
  onClickMenu,
  onJoin,
}) => {
  const { classes, cx } = useStyles();

  const [appointment] = useFragment(
    appointmentPanelFragment,
    `Appointment:${id}`
  );

  const isCurrent = useAppointmentCurrent(appointment);

  const startTime = format(
    { timeofday: appointment?.startTime ?? new Date() },
    "hh:mm a"
  );
  const endTime = format(
    { timeofday: appointment?.endTime ?? new Date() },
    "hh:mm a"
  );
  const date = format(
    { date: appointment?.startDate ?? new Date() },
    "iiii, dd MMM, yyyy"
  );

  const headerTitle = isCurrent ? "Current Appointment" : "Next Appointment";
  const headerColor = isCurrent ? "#FFE4A9" : "#D4DBE6";
  const profileColor = isCurrent ? "#FFF6E1" : colors.light;
  const online = appointment?.appointmentType === "online";

  return (
    <Box className={classes.container}>
      <Box
        className={cx(classes.contentSpacing, classes.header)}
        style={{ backgroundColor: headerColor }}
      >
        <Typography variant="body1" className={classes.headerTitle}>
          {headerTitle}
        </Typography>
      </Box>
      <Box
        component="div"
        className={cx(classes.profile)}
        style={{ backgroundColor: profileColor }}
      >
        <Box>
          <Avatar
            className={classes.avatar}
            src={appointment?.client.profile?.avatarUrl ?? undefined}
            alt="avatar"
          />
        </Box>
        <Box className={classes.detailsContainer}>
          <Button
            className={classes.badge}
            size="small"
            variant="outlined"
            startIcon={
              online ? (
                <CameraFrontIcon
                  className={classes.typeIcon}
                  htmlColor={colors.grey}
                />
              ) : (
                <PersonIcon
                  className={classes.typeIcon}
                  htmlColor={colors.grey}
                />
              )
            }
          >
            {online ? "Online Appointment" : "In-Person Appointment"}
          </Button>
          <Box padding={1} />
          <Typography variant="body1" className={classes.name}>
            {appointment?.client.profile?.fullName}
          </Typography>

          <Typography variant="caption" className={classes.contact}>
            {appointment?.client.email}
          </Typography>
          <br />
          <Typography variant="caption" className={classes.contact}>
            {appointment?.client.profile?.mobileNumber ?? ""}
          </Typography>
        </Box>
      </Box>
      <Box className={cx(classes.contentSpacing, classes.borderedContainer)}>
        <Box className={classes.appointmentTime}>
          <WatchLater htmlColor={colors.grey} />
          <Box>
            <Typography variant="body1" className={classes.dateTime}>
              {date}
            </Typography>
            <Typography variant="body1" className={classes.dateTime}>
              {startTime} - {endTime}
            </Typography>
            {Features.appointmentPanelDescriptionEnabled && (
              <>
                <Typography variant="body2" className={classes.aTitle}>
                  Discussion about weight gain diet plan
                </Typography>
                <Typography variant="body2" className={classes.aDescription}>
                  Lorem ipsum is placeholder text commonly used in the graphic,
                  print, and publishing industries.
                </Typography>
              </>
            )}
          </Box>
        </Box>
        <>
          <Box className={classes.actions}>
            {appointment?.appointmentType === "online" && (
              <StyledButton onClick={onJoin}>Join</StyledButton>
            )}
            <StyledButton onClick={onEdit}>Edit</StyledButton>
          </Box>
        </>
      </Box>
    </Box>
  );
};

export default AppointmentPanel;
